import React, { useState, useEffect } from "react";
import CustomCard from "../../components/CustomCard";
import CustomInput from "../../components/CustomInput";
import CustomDropdown from "../../components/CustomDropdown";
import CustomButton from "../../components/CustomButton";
import Popup from "../../components/Popup";
import "../css/InformationAccount.css";
import dropdownOptions from "../../data/dropdownOptions.json";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { checkAgentTradingAccount, fetchAndStoreServerPublicKey } from "../../utils/apiClient";
import AppConstants from "../../utils/appConstants";

export default function InformationAccount() {
  const navigate = useNavigate();
  const { broker_id } = useParams();
  const { t } = useTranslation();
  const hasBrokerIdInRoute = broker_id !== undefined && broker_id !== null && broker_id !== "";

  const [hasBroker, setHasBroker] = useState(false);
  const [brokerId, setBrokerId] = useState("");
  const [isBrokerLocked, setIsBrokerLocked] = useState(false);
  const [userType, setUserType] = useState(null);
  const [accountType, setAccountType] = useState(null);
  const [leverage, setLeverage] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCheckingBroker, setIsCheckingBroker] = useState(false);
  const [showInvalidBrokerPopup, setShowInvalidBrokerPopup] = useState(false);

  useEffect(() => {
    const initializeServerKey = async () => {
      try {
        await fetchAndStoreServerPublicKey();
      } catch (error) {
      }
    };
    initializeServerKey();

    // Check if there's saved data first - if there is, it's likely back navigation, not refresh
    const savedStep1Data = sessionStorage.getItem('registration_step1_data');
    
    // Check if this is a direct refresh on step 1
    let isPageRefresh = false;
    try {
      const navigationEntry = performance.getEntriesByType('navigation')[0];
      if (navigationEntry) {
        isPageRefresh = navigationEntry.type === 'reload';
      }
    } catch (e) {
      // Performance API not available, assume it's not a refresh
      isPageRefresh = false;
    }
    
    // Only treat as refresh if:
    // 1. Performance API says "reload" AND
    // 2. There's no saved data (or data is very old/stale)
    // If there's saved data, it's likely back navigation, not refresh
    if (isPageRefresh) {
      if (savedStep1Data) {
        // Check if data is fresh (saved within last 5 seconds) - indicates recent navigation
        try {
          const step1Data = JSON.parse(savedStep1Data);
          if (step1Data.timestamp) {
            const timeDiff = Date.now() - step1Data.timestamp;
            const isDataFresh = timeDiff < 5000; // Data saved within last 5 seconds
            
            if (isDataFresh) {
              // Data is fresh - this is likely back navigation, not refresh
              isPageRefresh = false; // Don't treat as refresh
            } else {
              // Data is old - this is likely a real refresh
              // Clear all session storage
              sessionStorage.removeItem('registration_step1_data');
              sessionStorage.removeItem('registration_step2_data');
              localStorage.removeItem('registration_accountType');
              localStorage.removeItem('registration_userType');
              // Reset all form state to initial values
              setAccountType(null);
              setUserType(null);
              setLeverage(null);
              setHasBroker(false);
              setBrokerId("");
              setErrors({});
              return;
            }
          } else {
            // No timestamp - treat as refresh to be safe
            sessionStorage.removeItem('registration_step1_data');
            sessionStorage.removeItem('registration_step2_data');
            localStorage.removeItem('registration_accountType');
            localStorage.removeItem('registration_userType');
            setAccountType(null);
            setUserType(null);
            setLeverage(null);
            setHasBroker(false);
            setBrokerId("");
            setErrors({});
            return;
          }
        } catch (e) {
          // Failed to parse - treat as refresh
          sessionStorage.removeItem('registration_step1_data');
          sessionStorage.removeItem('registration_step2_data');
          localStorage.removeItem('registration_accountType');
          localStorage.removeItem('registration_userType');
          setAccountType(null);
          setUserType(null);
          setLeverage(null);
          setHasBroker(false);
          setBrokerId("");
          setErrors({});
          return;
        }
      } else {
        // No saved data and Performance API says reload - definitely a refresh
        // Clear all session storage
        sessionStorage.removeItem('registration_step1_data');
        sessionStorage.removeItem('registration_step2_data');
        localStorage.removeItem('registration_accountType');
        localStorage.removeItem('registration_userType');
        // Reset all form state to initial values
        setAccountType(null);
        setUserType(null);
        setLeverage(null);
        setHasBroker(false);
        setBrokerId("");
        setErrors({});
        return;
      }
    }
    
    // If not a refresh, restore saved data (user came via back button or normal navigation)
    // This allows user to see and modify their previously entered data
    // savedStep1Data is already declared above
    if (savedStep1Data) {
      try {
        const step1Data = JSON.parse(savedStep1Data);
        
        // Restore accountType
        if (step1Data.accountType) {
          setAccountType(step1Data.accountType);
        }
        
        // Restore userType
        if (step1Data.userType) {
          setUserType(step1Data.userType);
        }
        
        // Restore leverage
        if (step1Data.leverage) {
          setLeverage(step1Data.leverage);
        }
        
        // Restore broker information ONLY if route parameter doesn't exist
        // Route parameter will override this in the next useEffect
        if (!broker_id || broker_id === "" || broker_id === undefined || broker_id === null) {
          if (step1Data.brokerId !== null && step1Data.brokerId !== undefined && step1Data.brokerId !== "") {
            setHasBroker(true);
            setBrokerId(step1Data.brokerId);
          } else {
            setHasBroker(false);
            setBrokerId("");
          }
        }
        
        // Mark that initial restore is complete
        setIsInitialRestore(false);
      } catch (e) {
        setIsInitialRestore(false);
      }
    } else {
      setIsInitialRestore(false);
    }
  }, []);

  // Apply broker_id from route param (if present) and lock broker field
  // Clear broker fields if parameter is not present
  useEffect(() => {
    if (broker_id !== undefined && broker_id !== null && broker_id !== "") {
      // Route parameter exists - set broker ID and lock it
      setHasBroker(true);
      setBrokerId(broker_id);
      setIsBrokerLocked(true);
    } else {
      // Route parameter doesn't exist - keep restored/manual broker value, only unlock editing
      setIsBrokerLocked(false);
    }
  }, [broker_id]);

  // Track if initial data has been restored to prevent auto-save from overwriting during restoration
  const [isInitialRestore, setIsInitialRestore] = useState(true);
  
  // Auto-save step 1 data whenever form fields change (so user can modify and it's preserved)
  useEffect(() => {
    // Skip auto-save during initial restore (first render)
    if (isInitialRestore) {
      // After a short delay, allow auto-save to work
      const timer = setTimeout(() => {
        setIsInitialRestore(false);
      }, 100);
      return () => clearTimeout(timer);
    }
    
    // Only save if we have at least one field filled (to avoid saving empty state)
    if (userType || accountType || leverage || (hasBroker && brokerId)) {
      const registrationData = {
        accountType: accountType,
        userType: userType,
        leverage: leverage,
        brokerId: hasBroker && brokerId ? brokerId : null,
        timestamp: Date.now()
      };
      sessionStorage.setItem('registration_step1_data', JSON.stringify(registrationData));
    }
  }, [userType, accountType, leverage, hasBroker, brokerId, isInitialRestore]);

  const handleNext = async () => {
    
    const newErrors = {};

    if (!userType) {
      newErrors.userType = t("informationAccount.clientType.label") + " is required";
    }
    if (!accountType) {
      newErrors.accountType = t("informationAccount.accountType.label") + " is required";
    }
    if (!leverage) {
      newErrors.leverage = t("informationAccount.leverage.label") + " is required";
    }
    if (hasBroker && !brokerId) {
      newErrors.brokerId = t("informationAccount.broker.idLabel") + " is required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }
    

    if (hasBroker && brokerId && brokerId.trim() !== "") {
      setIsCheckingBroker(true);
      try {
        const response = await checkAgentTradingAccount(brokerId);
        let decryptedData = response.decryptedData;
        if (typeof decryptedData === 'string') {
          try {
            decryptedData = JSON.parse(decryptedData);
          } catch (e) {
            const codeMatch = decryptedData.match(/"CODE"\s*:\s*(\d+)/);
            if (codeMatch) {
              decryptedData = { CODE: parseInt(codeMatch[1]) };
            } else {
              const codeMatch2 = decryptedData.match(/CODE["\s]*[:=]\s*(\d+)/i);
              if (codeMatch2) {
                decryptedData = { CODE: parseInt(codeMatch2[1]) };
              }
            }
          }
        }
      
        let responseCode = null;
        
        if (decryptedData) {
          responseCode = decryptedData.CODE || decryptedData.code;
                    if (responseCode === null || responseCode === undefined) {
            if (decryptedData.body) {
              responseCode = decryptedData.body.CODE || decryptedData.body.code;
            }
            if ((responseCode === null || responseCode === undefined) && decryptedData.data) {
              responseCode = decryptedData.data.CODE || decryptedData.data.code;
            }
          }
        }
        
        if (responseCode !== null && responseCode !== undefined) {
          responseCode = typeof responseCode === 'string' ? parseInt(responseCode) : responseCode;
        }
        
        if (responseCode === AppConstants.responseOk) {
          const registrationData = {
            accountType: accountType,
            userType: userType,
            leverage: leverage,
            brokerId: brokerId || null,
            timestamp: Date.now() // Add timestamp to track when data was saved
          };
          // Save data first
          sessionStorage.setItem('registration_step1_data', JSON.stringify(registrationData));
          // Also persist in localStorage so it can be used later in the flow (e.g., SignupSuccess)
          localStorage.setItem('registration_step1_data', JSON.stringify(registrationData));
          
          // Navigate to step 2
          navigate("/personalInformation", {
            state: {
              accountType: accountType,
              userType: userType
            }
          });
        } else if (responseCode === AppConstants.responseFailed) {
          setShowInvalidBrokerPopup(true);
        } else {
          setShowInvalidBrokerPopup(true);
        }
      } catch (error) {
        setShowInvalidBrokerPopup(true);
      } finally {
        setIsCheckingBroker(false);
      }
    } else {
      const registrationData = {
        accountType: accountType,                             
        userType: userType,
        leverage: leverage,               
        brokerId: null,
        timestamp: Date.now() // Add timestamp to track when data was saved
      };
      // Save data first
      sessionStorage.setItem('registration_step1_data', JSON.stringify(registrationData));
      // Also persist in localStorage so it can be used later in the flow (e.g., SignupSuccess)
      localStorage.setItem('registration_step1_data', JSON.stringify(registrationData));
      
      // Navigate to step 2
      navigate("/personalInformation", {
        state: {
          accountType: accountType,
          userType: userType
        }
      });
    }
  };

  // Clear error when field is changed
  const handleUserTypeChange = (value) => {
    setUserType(value);
    if (errors.userType) {
      setErrors({ ...errors, userType: null });
    }
  };

  const handleAccountTypeChange = (value) => {
    setAccountType(value);
    if (errors.accountType) {
      setErrors({ ...errors, accountType: null });
    }
  };

  const handleLeverageChange = (value) => {
    setLeverage(value);
    if (errors.leverage) {
      setErrors({ ...errors, leverage: null });
    }
  };

  const handleBrokerIdChange = (e) => {
    const value = e.target.value;
    setBrokerId(value);
    
    // Clear error when field is changed
    if (errors.brokerId) {
      setErrors({ ...errors, brokerId: null });
    }
  };
  const userTypeOptions = dropdownOptions.userTypeOptions.map((opt) => ({
    ...opt,
    label:
      t(`informationAccount.clientType.options.${opt.value}`) !==
        `informationAccount.clientType.options.${opt.value}`
        ? t(`informationAccount.clientType.options.${opt.value}`)
        : opt.label,
  }));

  const accountTypeOptions = dropdownOptions.accountTypeOptions.map((opt) => ({
    ...opt,
    label:
      t(`informationAccount.accountType.options.${opt.value}`) !==
        `informationAccount.accountType.options.${opt.value}`
        ? t(`informationAccount.accountType.options.${opt.value}`)
        : opt.label,
  }));

  const leverageOptions = dropdownOptions.leverageOptions.map((opt) => ({
    ...opt,
    label:
      t(`informationAccount.leverage.options.${opt.value}`) !==
        `informationAccount.leverage.options.${opt.value}`
        ? t(`informationAccount.leverage.options.${opt.value}`)
        : opt.label,
  }));

  return (
    <div className="page-container">
      <CustomCard className="registration-card">
        <div className="signup-content">
          <h1 className="signup-title">{t("informationAccount.title")}</h1>

          {/* Progress - Only step 1 active */}
          <div className="progress-indicator">
            <div className="progress-circle active">
              {t("informationAccount.progress.step1")}
            </div>
            <div className="progress-line" />
            <div className="progress-circle">
              {t("informationAccount.progress.step2")}
            </div>
            <div className="progress-line" />
            <div className="progress-circle">
              {t("informationAccount.progress.step3")}
            </div>
          </div>

          {/* Form */}
          <div className="signup-form">
            <div className="broker-row">
              <span>{t("informationAccount.broker.question")}</span>
              <input
                type="checkbox"
                checked={hasBroker}
                onChange={(e) => {
                  if (!isBrokerLocked) {
                    setHasBroker(e.target.checked);
                  }
                }}
                disabled={isBrokerLocked}
                className="custom-checkbox"
              />
            </div>

            {hasBroker && (
              <div className="form-group">
                <label>{t("informationAccount.broker.idLabel")}</label>
                <div className="input-wrapper">
                   <span className="input-icon">
                    <i className="fa-solid fa-user-tie"></i>
                  </span>
                  <input
                    type="number"
                    placeholder={t("informationAccount.broker.idPlaceholder")}
                    className={`input-box ${errors.brokerId ? "input-error" : ""}`}
                    value={brokerId}
                    onChange={isBrokerLocked ? undefined : handleBrokerIdChange}
                    readOnly={isBrokerLocked}
                    required
                  />
                </div>
              </div>
            )}

            <div className="form-group">
              <label>{t("informationAccount.clientType.label")}</label>
              <CustomDropdown
                options={userTypeOptions}
                value={userType}
                onChange={handleUserTypeChange}
                placeholder={t("informationAccount.clientType.placeholder")}
                required
                className={errors.userType ? "dropdown-error" : ""}
              />
            </div>

            <div className="form-group">
              <label>{t("informationAccount.accountType.label")}</label>
              <CustomDropdown
                options={accountTypeOptions}
                value={accountType}
                onChange={handleAccountTypeChange}
                placeholder={t("informationAccount.accountType.placeholder")}
                required
                className={errors.accountType ? "dropdown-error" : ""}
              />
            </div>

            <div className="form-group">
              <label>{t("informationAccount.leverage.label")}</label>
              <CustomDropdown
                options={leverageOptions}
                value={leverage}
                onChange={handleLeverageChange}
                placeholder={t("informationAccount.leverage.placeholder")}
                required
                className={errors.leverage ? "dropdown-error" : ""}
              />
            </div>

            <div className="button-group-vertical">
              <CustomButton
                className="next-button"
                onClick={handleNext}
                disabled={isCheckingBroker}
              >
                {isCheckingBroker ? "Validating..." : t("informationAccount.nextButton")}
              </CustomButton>
              {!hasBrokerIdInRoute && (
                <p className="back-to-login-row">
                  <span className="back-to-login-prefix">
                    {t("informationAccount.backToLoginPrefix")}
                  </span>
                  <button
                    type="button"
                    className="back-to-login-link"
                    onClick={() => navigate("/home")}
                    disabled={isCheckingBroker}
                  >
                    {t("informationAccount.backToLoginLink")}
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      </CustomCard>
      
      <Popup
        isOpen={showInvalidBrokerPopup}
        onClose={() => setShowInvalidBrokerPopup(false)}
        title="Invalid Broker ID"
        message="The broker ID you entered is invalid. Please check and try again."
        buttonText="OK"
        icon="fa-circle-exclamation"
      />
    </div>
  );
}
